import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";

import { getEventDepartment } from "../utils/sharedEventsUtils";

function CarteFrance({ events = [], onDepartmentSelect }) {
  const [departements, setDepartements] = useState([]);
  const [loadingError, setLoadingError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadGeoJSON = async () => {
      try {
        const response = await fetch("/departements.geojson");

        if (!response.ok) {
          throw new Error("Impossible de charger /departements.geojson");
        }

        const data = await response.json();

        if (isMounted) {
          setDepartements(data.features || []);
        }
      } catch (error) {
        console.error("Erreur lors du chargement du GeoJSON :", error);

        if (isMounted) {
          setLoadingError(true);
        }
      }
    };

    loadGeoJSON();

    return () => {
      isMounted = false;
    };
  }, []);

  const departementsAvecEvenements = useMemo(() => {
    return new Set(events.map((event) => getEventDepartment(event)).filter(Boolean));
  }, [events]);

  const styleDepartement = (feature) => {
    const codeDepartement = feature?.properties?.code;
    const isHighlighted = departementsAvecEvenements.has(codeDepartement);

    return {
      fillColor: isHighlighted ? "#0284c7" : "#cbd5e1",
      weight: isHighlighted ? 2 : 1,
      color: isHighlighted ? "#075985" : "#64748b",
      fillOpacity: isHighlighted ? 0.65 : 0.35,
    };
  };

  const onEachFeature = (feature, layer) => {
    const codeDepartement = feature?.properties?.code;
    const nomDepartement = feature?.properties?.nom;
    const isHighlighted = departementsAvecEvenements.has(codeDepartement);

    layer.bindTooltip(`${codeDepartement} - ${nomDepartement}`, {
      direction: "top",
      permanent: false,
    });

    if (isHighlighted) {
      layer.on({
        click: () => onDepartmentSelect(codeDepartement),
        mouseover: () => {
          layer.setStyle({
            fillOpacity: 0.85,
            weight: 3,
          });
        },
        mouseout: () => {
          layer.setStyle(styleDepartement(feature));
        },
      });
    }
  };

  if (loadingError) {
    return (
      <div className="rounded-[1.5rem] border border-red-200 bg-red-50 p-5 text-sm font-bold text-red-700 dark:border-red-400/20 dark:bg-red-500/10 dark:text-red-100">
        Impossible de charger la carte. Vérifie que le fichier
        <span className="mx-1 rounded bg-white px-2 py-1 dark:bg-white/10">
          /departements.geojson
        </span>
        est bien disponible dans le dossier public.
      </div>
    );
  }

  if (!departements.length) {
    return (
      <div className="flex h-[420px] items-center justify-center rounded-[1.5rem] bg-slate-100 text-sm font-bold text-slate-500 dark:bg-white/10 dark:text-white/60">
        Chargement de la carte...
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-slate-100 dark:border-white/10 dark:bg-white/[0.05]">
      <MapContainer
        center={[46.603354, 1.888334]}
        zoom={6}
        style={{ height: "min(70vh, 620px)", width: "100%" }}
        className="z-0"
        scrollWheelZoom
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        <GeoJSON
          data={{
            type: "FeatureCollection",
            features: departements,
          }}
          style={styleDepartement}
          onEachFeature={onEachFeature}
        />
      </MapContainer>
    </div>
  );
}

export default CarteFrance;